import React, { useEffect, useState } from "react";
import Input from "../common/Input";
import Alert from "../common/Alert";
import Button from "../common/Button";
import Modal from "../common/Modal";
import { getErrorMessage } from "../../utils/errorMessage";
import { normalizeMoneyAmount } from "../../utils/money";

/**
 * PaymentMethodSelector Component
 * Payment method selection with optional custom amount
 *
 * @param {Object} props
 * @param {number} props.totalAmount - Total amount to pay
 * @param {Function} props.onPay - Payment callback (method, amount, tipAmount)
 * @param {boolean} [props.isLoading] - Loading state
 * @param {Object} [props.error] - Error object
 * @param {boolean} [props.allowPartialPayment] - Allow custom amount
 * @param {boolean} [props.compact] - Compact modal-friendly layout
 * @param {boolean} [props.showSummary] - Show the amount summary block
 * @param {Function} [props.onTipChange] - Notifies parent when selected tip changes
 * @param {{enabled?: boolean, percentages?: number[]}} [props.tipConfig] - Tip configuration
 */
const PaymentMethodSelector = ({
  totalAmount,
  onPay,
  isLoading = false,
  error = null,
  allowPartialPayment = false,
  compact = false,
  showSummary = true,
  onTipChange,
  tipConfig,
}) => {
  const [customAmount, setCustomAmount] = useState("");
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [tipPercentage, setTipPercentage] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [customTipDraft, setCustomTipDraft] = useState("");
  const [showCustomTipModal, setShowCustomTipModal] = useState(false);
  const resolvedTipConfig = {
    enabled: tipConfig?.enabled ?? true,
    percentages: Array.isArray(tipConfig?.percentages)
      ? tipConfig.percentages
      : [15, 18, 20],
  };
  const normalizedTipPercentages = Array.from(
    new Set(
      Array.isArray(resolvedTipConfig.percentages)
        ? resolvedTipConfig.percentages
            .map((value) => Number(String(value || "").trim()))
            .filter(
              (value) =>
                Number.isFinite(value) &&
                Number.isInteger(value) &&
                value > 0,
            )
        : [],
    ),
  ).slice(0, 3);

  const tipAmount = normalizeMoneyAmount(
    tipPercentage > 0
      ? (totalAmount * tipPercentage) / 100
      : parseFloat(customTip) || 0,
  );

  const finalAmount = normalizeMoneyAmount(totalAmount + tipAmount);

  useEffect(() => {
    if (typeof onTipChange === "function") {
      onTipChange(tipAmount);
    }
  }, [tipAmount, onTipChange]);

  useEffect(() => {
    if (!resolvedTipConfig.enabled) {
      setTipPercentage(0);
      setCustomTip("");
      setCustomTipDraft("");
      setShowCustomTipModal(false);
      return;
    }
  }, [resolvedTipConfig.enabled]);

  const handlePay = (method) => {
    const amount = normalizeMoneyAmount(
      useCustomAmount && customAmount ? parseFloat(customAmount) : finalAmount,
    );

    // Validation
    if (useCustomAmount) {
      if (isNaN(amount) || amount <= 0) {
        return;
      }
      if (!amountMatchesTotal) {
        return;
      }
    }

    onPay(method, amount, tipAmount);
  };

  const openCustomTipModal = () => {
    setTipPercentage(0);
    setCustomTipDraft(customTip || "");
    setShowCustomTipModal(true);
  };

  const closeCustomTipModal = () => {
    setShowCustomTipModal(false);
    setCustomTipDraft(customTip || "");
  };

  const appendCustomTipValue = (nextValue) => {
    setCustomTipDraft((current) => {
      const currentValue = String(current || "");

      if (nextValue === ".") {
        return currentValue.includes(".") ? currentValue : `${currentValue || "0"}.`;
      }

      if (nextValue === "BACKSPACE") {
        return currentValue.slice(0, -1);
      }

      if (nextValue === "CLEAR") {
        return "";
      }

      return `${currentValue}${nextValue}`;
    });
  };

  const applyCustomTip = () => {
    const nextValue = String(customTipDraft || "").trim();
    setTipPercentage(0);
    setCustomTip(nextValue);
    setShowCustomTipModal(false);
  };

  const clearTipSelection = () => {
    setTipPercentage(0);
    setCustomTip("");
    setCustomTipDraft("");
    setShowCustomTipModal(false);
  };

  const amount = normalizeMoneyAmount(
    useCustomAmount && customAmount ? parseFloat(customAmount) : totalAmount,
  );
  const expectedAmount = normalizeMoneyAmount(totalAmount);
  const amountMatchesTotal = amount === expectedAmount;

  const isValidAmount =
    !useCustomAmount || (!isNaN(amount) && amount > 0 && amountMatchesTotal);

  return (
    <div className={compact ? "space-y-4" : "space-y-6"}>
      {/* Amount Display with Tip */}
      {showSummary && (
        <div
          className={
            compact
              ? "rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 p-3"
              : "rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 p-6 text-white shadow-lg"
          }
        >
          <div
            className={
              compact
                ? "flex items-center justify-between"
                : "block"
            }
          >
            <div
              className={
                compact
                  ? "text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
                  : "text-sm font-medium opacity-90 mb-1"
              }
            >
              Amount to Pay
            </div>
            <div
              className={
                compact
                  ? "text-xl font-black text-indigo-600 dark:text-indigo-400"
                  : "text-4xl font-bold"
              }
            >
              ${(useCustomAmount && customAmount ? amount : finalAmount).toFixed(2)}
            </div>
          </div>
          {tipAmount > 0 && (
            <div className="text-sm opacity-90 mt-2 flex items-center justify-between">
              <span>Subtotal: ${totalAmount.toFixed(2)}</span>
              <span>Tip: ${tipAmount.toFixed(2)}</span>
            </div>
          )}
          {useCustomAmount && customAmount && !amountMatchesTotal && (
            <div className="text-sm opacity-90 mt-2">
              Custom amount must match the total exactly.
            </div>
          )}
        </div>
      )}

      {/* Tip Selection */}
      {resolvedTipConfig.enabled && !useCustomAmount && (
        <div className="space-y-3">
          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
            Add Tip (Optional)
          </label>
          {normalizedTipPercentages.length > 0 && (
            <div
              className={`grid gap-2 ${
                normalizedTipPercentages.length === 1
                  ? "grid-cols-1"
                  : normalizedTipPercentages.length === 2
                    ? "grid-cols-2"
                    : "grid-cols-3"
              }`}
            >
              {normalizedTipPercentages.map((percent) => (
                <button
                  key={percent}
                  type="button"
                  onClick={() => {
                    if (tipPercentage === percent) {
                      setTipPercentage(0);
                    } else {
                      setTipPercentage(percent);
                    }
                    setCustomTip("");
                  }}
                  className={`
                    py-3 rounded-xl font-bold text-sm transition-all
                    ${
                      tipPercentage === percent
                        ? "bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg scale-105"
                        : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                    }
                  `}
                >
                  <div>{percent}%</div>
                  <div className="text-xs opacity-75">
                    ${((totalAmount * percent) / 100).toFixed(2)}
                  </div>
                </button>
              ))}
            </div>
          )}
          <div
            role="button"
            tabIndex={0}
            onClick={openCustomTipModal}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openCustomTipModal();
              }
            }}
            className={`w-full rounded-xl border-2 px-4 py-3 text-left transition ${
              customTip
                ? "border-indigo-400 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-950/30 dark:text-indigo-200"
                : "border-slate-200 bg-white text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold">
                  {customTip
                    ? `Custom tip: $${Number(customTip).toFixed(2)}`
                    : "Custom"}
                </div>
                <div className="text-xs opacity-80">
                  Tap to enter a custom tip amount
                </div>
              </div>
              {customTip && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearTipSelection();
                  }}
                  className="shrink-0 whitespace-nowrap"
                >
                  Clear Tip
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Amount (if allowed) */}
      {allowPartialPayment && (
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useCustomAmount}
              onChange={(e) => setUseCustomAmount(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Pay custom amount
            </span>
          </label>

          {useCustomAmount && (
            <Input
              label="Custom Amount"
              type="number"
              step="0.01"
              min="0.01"
              max={totalAmount}
              placeholder="0.00"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              helper={`Maximum: $${totalAmount.toFixed(2)}`}
            />
          )}
        </div>
      )}

      {/* Payment Method Buttons */}
      <div className={compact ? "space-y-2" : "space-y-3"}>
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
          Select Payment Method
        </h3>

        {/* Cash Button */}
        <button
          type="button"
          onClick={() => handlePay("CASH")}
          disabled={isLoading || !isValidAmount}
          className={`w-full rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-emerald-500 dark:hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition group ${
            compact ? "p-3" : "p-4"
          }`}
        >
          <div className={`flex items-center ${compact ? "gap-3" : "gap-4"}`}>
            <div
              className={`rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center group-hover:bg-emerald-200 dark:group-hover:bg-emerald-900/50 transition ${
                compact ? "w-10 h-10" : "w-12 h-12"
              }`}
            >
              <svg
                className={`text-emerald-600 dark:text-emerald-400 ${
                  compact ? "h-5 w-5" : "h-6 w-6"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <div className="font-semibold text-slate-900 dark:text-slate-100">
                Cash
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Pay with cash
              </div>
            </div>
            <svg
              className="h-5 w-5 text-slate-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </button>

        {/* Card Button */}
        <button
          type="button"
          onClick={() => handlePay("CARD")}
          disabled={isLoading || !isValidAmount}
          className={`w-full rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-indigo-500 dark:hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition group ${
            compact ? "p-3" : "p-4"
          }`}
        >
          <div className={`flex items-center ${compact ? "gap-3" : "gap-4"}`}>
            <div
              className={`rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center group-hover:bg-indigo-200 dark:group-hover:bg-indigo-900/50 transition ${
                compact ? "w-10 h-10" : "w-12 h-12"
              }`}
            >
              <svg
                className={`text-indigo-600 dark:text-indigo-400 ${
                  compact ? "h-5 w-5" : "h-6 w-6"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <div className="font-semibold text-slate-900 dark:text-slate-100">
                Card
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Credit or debit card
              </div>
            </div>
            <svg
              className="h-5 w-5 text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </button>
      </div>

      {/* Validation Error */}
      {useCustomAmount && customAmount && !amountMatchesTotal && (
        <Alert tone="warning">
          Payment amount must match the total amount exactly.
        </Alert>
      )}

      {/* API Error */}
      {error && (
        <Alert tone="error">
          {getErrorMessage(error, "Payment failed. Please try again.")}
        </Alert>
      )}

      <Modal
        isOpen={showCustomTipModal}
        onClose={closeCustomTipModal}
        title="Custom Tip"
        size="md"
      >
        <div className="space-y-3">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 p-3 text-center">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Tip Amount
            </div>
            <div className="mt-1 text-3xl font-black text-indigo-600 dark:text-indigo-400">
              ${Number(customTipDraft || 0).toFixed(2)}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2.5">
            <div className="grid grid-cols-3 gap-2.5">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "BACKSPACE"].map(
                (key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => appendCustomTipValue(key)}
                    className={`h-12 rounded-lg border text-lg font-semibold transition active:scale-[0.98] ${
                      key === "BACKSPACE"
                        ? "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                        : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                    }`}
                  >
                    {key === "BACKSPACE" ? "DEL" : key}
                  </button>
                ),
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={closeCustomTipModal}>
              Cancel
            </Button>
            <Button type="button" onClick={applyCustomTip}>
              Done
            </Button>
          </div>
        </div>
      </Modal>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Processing payment...
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodSelector;
